import {
  RadioTileGroup,
  IRadioTileOption,
} from '@noissue-ui-kit/oldBranding/radioTile'
import {
  DropdownSelect,
  IDropdownOption,
} from '@noissue-ui-kit/oldBranding/dropdownSelect'
import { ProductVariant, SelectFn, SelectorConfiguration } from '../types'
import {
  IQuantitySelectorOption,
  QuantitySelector,
} from '@components/common/QuantitySelector'
import {
  calculateUnitPrice,
  calculateGridOverrideClass,
} from './VariantOptionSelection.utils'
import { trackDesignerInteraction } from '@utils/gtm'
import { useSearchParams } from 'next/navigation'
import { useCommerce } from '@commerce'
import Link from 'next/link'
import {
  COLOR_GUIDELINES_LINK,
  PAPER_TYPES_ARTICLE_LINK,
  SIZE_GUIDELINES_LINK,
} from '@utils/client-side-config'
import { FormFieldTooltip } from '@noissue-ui-kit/form/form/FormFieldTooltip'
import { Switch } from '@noissue-ui-kit/switch'
import { useState } from 'react'
import {
  applyRegionSpecificUnits,
  getPreferredUnitsSystem,
  TUnitsSystem,
  UNIT_SYSTEMS,
} from './utils/formatOptionLabel'

type radioTileDisplayType = 'RadioTile'
type dropdownDisplayType = 'Dropdown'
type quantitySelectorDisplayType = 'QuantitySelector'

type IVariantOptionSelection = {
  selectedValue: IRadioTileOption | IDropdownOption | IQuantitySelectorOption
  selectChange: SelectFn
  option: SelectorConfiguration
  getQuantityOptions: () => {
    quantityOptions: ProductVariant[]
    sampleOptions: ProductVariant[]
    quantityOptionsWithoutSamples: ProductVariant[]
  }
  readOnlyMode?: boolean
  showLabel?: boolean
  onError?: ({ field, error }: { field: string; error: string }) => void
  customQuantity?: number
  setCustomQuantity?: (customQuantity: number) => void
  minimumQuoteQuantityOverride?: number
  requestAQuoteFormPath?: string
  alternativeDtcPagePath?: string
  includeQuoteOption?: boolean
  requestAQuoteFlowEnabled?: boolean
  extraQuoteQuantityOptions?: string[]
}

export function VariantOptionSelection({
  selectedValue,
  selectChange,
  option,
  getQuantityOptions,
  readOnlyMode = false,
  showLabel = true,
  onError,
  customQuantity,
  setCustomQuantity,
  minimumQuoteQuantityOverride,
  requestAQuoteFormPath,
  alternativeDtcPagePath,
  includeQuoteOption = false,
  requestAQuoteFlowEnabled = false,
  extraQuoteQuantityOptions = [],
}: IVariantOptionSelection) {
  const searchParams = useSearchParams()
  const { locale } = useCommerce()

  const preferredMetricSystem: TUnitsSystem = getPreferredUnitsSystem()
  const [fieldModifiers, setFieldModifiers] = useState<Record<string, boolean>>(
    {}
  )

  function trackInteraction({
    selectedOptionName,
    selectedOptionValue,
    displayType,
  }) {
    trackDesignerInteraction({
      event: 'designer-interaction',
      designerName: 'upload',
      componentName: selectedOptionName,
      componentInformation: selectedOptionValue,
      selectedProductSlug: searchParams.get('slug'),
      displayType,
    })
  }

  function renderOptionSelector() {
    const variantDisplayType = option.type as
      | radioTileDisplayType
      | dropdownDisplayType
      | quantitySelectorDisplayType
    const optionName = option.name
    switch (variantDisplayType) {
      case 'RadioTile': {
        const variantOptions = option.values.map((value) => ({
          key: value,
          displayLabel: option.displayRegionSpecificUnits
            ? applyRegionSpecificUnits({
                optionName: option.name,
                label: value,
                fieldModifiers: fieldModifiers,
              })
            : value,
        }))
        return (
          <RadioTileGroup
            options={variantOptions as IRadioTileOption[]}
            optionName={optionName}
            onChange={(event) => {
              if (!readOnlyMode) {
                selectChange({
                  name: optionName,
                  value: event.key,
                })
                trackInteraction({
                  selectedOptionName: optionName,
                  selectedOptionValue: event.key,
                  displayType: 'RadioTile',
                })
              }
            }}
            selected={selectedValue}
            radioGroupClassName={`${calculateGridOverrideClass(
              variantOptions
            )} ${readOnlyMode ? 'pointer-events-none' : ''}`}
          />
        )
      }
      case 'Dropdown': {
        const variantOptions = option.values.map((value) => ({
          key: value,
          displayLabel: option.displayRegionSpecificUnits
            ? applyRegionSpecificUnits({
                optionName: option.name,
                label: value,
                fieldModifiers: fieldModifiers,
              })
            : value,
        }))

        const selectedOption = {
          key: selectedValue?.key,
          displayLabel: option.displayRegionSpecificUnits
            ? applyRegionSpecificUnits({
                optionName: option.name,
                label: selectedValue?.displayLabel as string,
                fieldModifiers: fieldModifiers,
              })
            : selectedValue.displayLabel,
        }

        return (
          <DropdownSelect
            options={variantOptions as IDropdownOption[]}
            onChange={(event) => {
              if (!readOnlyMode) {
                selectChange({
                  name: optionName,
                  value: event.key,
                })
                trackInteraction({
                  selectedOptionName: optionName,
                  selectedOptionValue: event.key,
                  displayType: 'Dropdown',
                })
              }
            }}
            selected={selectedOption as IDropdownOption}
            optionName={option.title}
            className={`${readOnlyMode ? 'pointer-events-none' : ''}`}
            optionsListClassName="relative"
          />
        )
      }
      case 'QuantitySelector': {
        const { sampleOptions, quantityOptionsWithoutSamples } =
          getQuantityOptions()

        const sampleOptionsMapped = sampleOptions?.map((option) => ({
          key: option?.quantity,
          displayLabel: option?.quantity,
          price: {
            originalPrice: option?.price,
            salePrice: option?.salePrice,
            currencyCode: option?.currencyCode,
          },
          discount: calculateUnitPrice({
            priceOfProduct:
              option.salePrice > 0 ? option.salePrice : option.price,
            qtyOfProduct: 1,
            locale,
          }),
        }))

        const quantityOptionsWithoutSamplesMapped =
          quantityOptionsWithoutSamples.map((value) => {
            return {
              key: value.quantity,
              displayLabel: value.quantity,
              price: {
                originalPrice: value?.price,
                salePrice: value?.salePrice,
                currencyCode: value?.currencyCode,
              },
              discount: calculateUnitPrice({
                priceOfProduct:
                  value.salePrice > 0 ? value.salePrice : value.price,
                qtyOfProduct: value.quantity,
                locale,
              }),
            }
          })

        const quoteQuantityOptions: IQuantitySelectorOption[] =
          extraQuoteQuantityOptions?.map((option) => {
            return {
              displayLabel: option,
              optionType: 'Plus',
              key: option?.trim(),
            }
          })

        const variantOptions = [
          ...sampleOptionsMapped,
          ...quantityOptionsWithoutSamplesMapped,
          ...quoteQuantityOptions,
        ]

        return (
          <QuantitySelector
            options={variantOptions as IQuantitySelectorOption[]}
            onChange={(event) => {
              if (!readOnlyMode) {
                selectChange({
                  name: optionName,
                  value: event?.key,
                })
                trackInteraction({
                  selectedOptionName: optionName,
                  selectedOptionValue: event?.key,
                  displayType: 'QuantitySelector',
                })
              }
            }}
            selected={selectedValue as IQuantitySelectorOption}
            className={`${readOnlyMode ? 'pointer-events-none' : ''}`}
            onError={(error) => {
              onError && onError({ field: optionName, error })
            }}
            setCustomQuantity={setCustomQuantity}
            customQuantity={customQuantity}
            minimumQuoteQuantityOverride={minimumQuoteQuantityOverride}
            requestAQuoteFormPath={requestAQuoteFormPath}
            alternativeDtcPagePath={alternativeDtcPagePath}
            includeQuoteOption={includeQuoteOption}
            requestAQuoteFlowEnabled={requestAQuoteFlowEnabled}
          />
        )
      }
      default:
        break
    }
  }

  const renderLabelSwitch = (option: SelectorConfiguration) => {
    const fieldName = option.name

    switch (fieldName) {
      case 'size': {
        return option.displayRegionSpecificUnits ? (
          <Switch
            id="size-units-switch"
            onLabel={
              preferredMetricSystem === UNIT_SYSTEMS.METRIC ? 'in' : 'mm'
            }
            offLabel={
              preferredMetricSystem === UNIT_SYSTEMS.METRIC ? 'mm' : 'in'
            }
            className="ml-4 mr-2"
            onChange={(checked) => {
              setFieldModifiers({
                ...fieldModifiers,
                [fieldName]: checked,
              })
            }}
          />
        ) : null
      }
    }
  }

  const renderLabelHint = (fieldName: string, hasTooltip: boolean = false) => {
    switch (fieldName) {
      case 'size': {
        return (
          <FormFieldTooltip>
            <p className="text-boulder text-xl normal-case">
              Size = Length (L) x Width (W) x Height/Depth (H/D). For visual
              reference&nbsp;
              <Link
                href={SIZE_GUIDELINES_LINK}
                className="text-acai underline"
                target="_blank"
              >
                see size diagrams
              </Link>
              .&nbsp;Production can vary by +/- 10%.
            </p>
          </FormFieldTooltip>
        )
      }

      case 'color':
      case 'colors': {
        return (
          <FormFieldTooltip>
            <p className="text-boulder text-xl normal-case">
              Need help choosing the right color option for your design?{' '}
              <Link
                href={COLOR_GUIDELINES_LINK}
                className="text-acai underline"
                target="_blank"
              >
                See our help article
              </Link>
            </p>
          </FormFieldTooltip>
        )
      }

      case 'type': {
        return (
          hasTooltip && (
            <FormFieldTooltip>
              <p className="text-boulder text-xl normal-case">
                For more info on our paper types{' '}
                <Link
                  href={PAPER_TYPES_ARTICLE_LINK}
                  className="text-acai underline"
                  target="_blank"
                >
                  see here
                </Link>
              </p>
            </FormFieldTooltip>
          )
        )
      }

      case 'number of designs': {
        return (
          <FormFieldTooltip>
            <p className="text-boulder text-xl normal-case">
              Multiple designs at no extra cost. 1,000 unit minimum per design
            </p>
          </FormFieldTooltip>
        )
      }

      default:
        return null
    }
  }

  return (
    <div className="mb-5">
      {showLabel && (
        <div
          className="flex flex-row items-center mb-5 text-xl capitalize text-acai font-mori leading-0 select-none"
          aria-label={`select ${option.title.toLowerCase()}`}
        >
          <span className="text-xl leading-none pt-2">{option.title}</span>
          {renderLabelSwitch(option)}
          {renderLabelHint(option.name, option.hasTooltip)}
        </div>
      )}
      {renderOptionSelector()}
    </div>
  )
}
